import React, { lazy, Suspense, memo, useMemo } from 'react'
import { useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import { Helmet } from 'react-helmet-async';
import Loading from 'components/shared-components/Loading';
import { lightTheme, darkTheme } from 'configs/ThemeConfig';
import { resources } from 'lang';
import useBodyClass from 'utils/hooks/useBodyClass';
import { useMyContext } from 'Context/MyContextProvider';
import { useLocation } from 'react-router-dom';
import { publicRoutes } from 'configs/RoutesConfig';
import Routes from 'routes';

const AppLayout = lazy(() => import('./AppLayout'));
const AuthLayout = lazy(() => import('./AuthLayout'));

const Layouts = () => {

	const token = useSelector(state => state?.auth?.token);
	const blankLayout = useSelector(state => state.theme?.blankLayout);

	// const Layout = token && !blankLayout ? AppLayout : AuthLayout;

	const locale = useSelector(state => state.theme?.locale);

	const direction = useSelector(state => state.theme?.direction);

	const currentTheme = useSelector(state => state.theme?.currentTheme);

	const currentAppLocale = resources[locale];

	useBodyClass(`dir-${direction}`);

	const themeConfig = currentTheme === 'light' ? { ...lightTheme } : { ...darkTheme }
	const { brandingData } = useMyContext();

	// Parse copyright data safely
	const copyright = useMemo(() => {
		if (!brandingData?.copyright) return {};

		// If it's already an object, return it
		if (typeof brandingData.copyright === 'object') {
			return brandingData.copyright;
		}

		// If it's a string, try to parse it
		if (typeof brandingData.copyright === 'string') {
			try {
				return JSON.parse(brandingData.copyright);
			} catch (error) {
				console.warn('Failed to parse copyright data:', error);
				return {};
			}
		}

		return {};
	}, [brandingData?.copyright]);

	// Determine site title based on branding data
	const siteTitle = useMemo(() => {
		if (brandingData && Object.keys(brandingData).length > 0 && copyright?.label) {
			return `Whatsapp | ${copyright.label}`;
		} else {
			return 'Whatsapp | SMS For You';
		}
	}, [brandingData, copyright]);



	//new code for active public routes for all users
	const location = useLocation();
	const { isMainServer } = useMyContext();
	// Check if branding data exists
	const hasBrandingData = brandingData && Object.keys(brandingData).length > 0;

	const isPublicRoute = useMemo(() => {
		if (!isMainServer) return false;

		return publicRoutes.some(route => {
			// Handle exact matches and dynamic routes
			if (route.path === location.pathname) return true;

			// Handle dynamic routes like /pricing/plan-detail/:id
			const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
			const regex = new RegExp(`^${routePattern}$`);
			return regex.test(location.pathname);
		});
	}, [location.pathname, isMainServer]);

	const Layout = useMemo(() => {
		// If it's a public route, always use AuthLayout (no sidebar)
		if (isPublicRoute) {
			return AuthLayout;
		}

		// For non-public routes, use the original logic
		return token && !blankLayout ? AppLayout : AuthLayout;
	}, [token, blankLayout, isPublicRoute]);
	return (
		<>
			{hasBrandingData && (
				<Helmet>
					<title>{siteTitle}</title>
					{copyright?.label && (
						<meta name="copyright" content={`© ${new Date().getFullYear()} ${copyright.label}. All rights reserved.`} />
					)}
					{brandingData?.label && (
						<meta name="author" content={brandingData.label} />
					)}
				</Helmet>
			)}
			<ConfigProvider theme={themeConfig} direction={direction} locale={currentAppLocale?.antd}>
				<Suspense fallback={<Loading cover="content" />}>
					<Layout>
						<Routes />
					</Layout>
				</Suspense>
			</ConfigProvider>
		</>
	)
}

export default memo(Layouts)